import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import { IManagedDbColumnFilter, ManagedDbFilterOperator } from "../../services/table-editor-service";

// KOA-5857 — Pure filter helpers shared between the grid filter row, the
// query hook, and the export hook.
//
// Lives outside editable-data-grid.tsx so unit tests can import the
// helpers without loading React, jsx-runtime, or any other UI module.

// Per-column filter entry. The value is unused for `isNull` / `isNotNull`
// (those operators bind no parameter). For every other operator an empty
// value means "no filter" — the query/export hooks drop the entry before
// sending it to the server.
export interface IColumnFilterEntry {
  operator: ManagedDbFilterOperator;
  value: string;
}

// Per-column filter map. Named index-signature interface per CLAUDE.md
// (Record<string, IColumnFilterEntry> is banned). Absence (or undefined)
// means "no filter for this column".
export interface IColumnFilterMap {
  [columnName: string]: IColumnFilterEntry | undefined;
}

// True when the operator produces a SQL clause without a bound value
// (IS NULL / IS NOT NULL). Used by the grid filter row to suppress the
// value input and by the query/export hooks to keep the filter even
// though its value string is empty.
export const isValuelessOperator = (op: ManagedDbFilterOperator): boolean => op === "isNull" || op === "isNotNull";

export interface IOperatorOption {
  value: ManagedDbFilterOperator;
  symbol: string;
  label: string;
}

export const ALL_FILTER_OPERATORS: readonly IOperatorOption[] = [
  { value: "eq", symbol: "=", label: "equals" },
  { value: "neq", symbol: "≠", label: "not equal" },
  { value: "gt", symbol: ">", label: "greater than" },
  { value: "gte", symbol: "≥", label: "greater or equal" },
  { value: "lt", symbol: "<", label: "less than" },
  { value: "lte", symbol: "≤", label: "less or equal" },
  { value: "contains", symbol: "∋", label: "contains" },
  { value: "notContains", symbol: "∌", label: "does not contain" },
  { value: "startsWith", symbol: "^", label: "starts with" },
  { value: "endsWith", symbol: "$", label: "ends with" },
  // `in` / `notIn` take a comma-separated value list ("A, B, C"); the
  // server splits and binds one parameter per token. Cap at 50 values.
  { value: "in", symbol: "∈", label: "is one of" },
  { value: "notIn", symbol: "∉", label: "is not one of" },
  { value: "isNull", symbol: "∅", label: "is empty" },
  { value: "isNotNull", symbol: "≠∅", label: "not empty" }
];

// Operators that only make sense on text-shaped columns. Hidden from the
// picker on Boolean / numeric / date / Guid / binary columns so the user
// can't accidentally compose a clause that always returns zero rows.
const TEXT_ONLY_OPERATORS: ReadonlySet<ManagedDbFilterOperator> = new Set<ManagedDbFilterOperator>([
  "contains",
  "notContains",
  "startsWith",
  "endsWith"
]);

// Comparison operators (gt / gte / lt / lte) only make sense on
// orderable types — numeric, date, datetime, datetimeoffset. Hidden on
// Boolean, Image, Object, Location, Signature, FileContent, byte arrays.
const ORDERABLE_KUIKA_TYPES: ReadonlySet<string> = new Set<string>([
  "Int16",
  "Int32",
  "Int64",
  "AutoIncrement",
  "Decimal",
  "Double",
  "Date",
  "Datetime",
  "DatetimeOffset"
]);

// Operators that aren't meaningful on a Boolean column (which has only
// two values and no ordering): everything except eq / neq / isNull /
// isNotNull. Hides contains / starts / etc. so the picker doesn't offer
// "starts with true".
const BOOLEAN_ALLOWED_OPERATORS: ReadonlySet<ManagedDbFilterOperator> = new Set<ManagedDbFilterOperator>([
  "eq",
  "neq",
  "isNull",
  "isNotNull"
]);

// Binary blob types — Image / Signature / FileContent / ByteArray /
// VarBinary / Binary / Bytea. The values are multi-MB base64 / data
// URIs, so eq / neq / in / notIn are technically valid SQL but
// useless in practice — no one types a base64 payload into a filter
// input. The only filter actually worth keeping is "is empty" /
// "not empty", which answers "which rows have an image / signature
// / file?" — the most common DB-tool query for these columns.
const BINARY_BLOB_KUIKA_TYPES: ReadonlySet<string> = new Set<string>([
  "Image",
  "Signature",
  "FileContent",
  "ByteArray",
  "VarBinary",
  "Binary",
  "Bytea"
]);

const isBinaryBlobKuikaType = (kuikaType: string | null | undefined): boolean => {
  if (!kuikaType) {
    return false;
  }
  return BINARY_BLOB_KUIKA_TYPES.has(kuikaType);
};

const BLOB_ALLOWED_OPERATORS: ReadonlySet<ManagedDbFilterOperator> = new Set<ManagedDbFilterOperator>([
  "isNull",
  "isNotNull"
]);

const isOrderableKuikaType = (kuikaType: string | null | undefined): boolean => {
  if (!kuikaType) {
    return true;
  }
  return ORDERABLE_KUIKA_TYPES.has(kuikaType);
};

const isTextLikeKuikaType = (kuikaType: string | null | undefined): boolean => {
  if (!kuikaType) {
    return true;
  }
  // String + Guid are "text-shaped" for filter purposes — the backend's
  // LIKE / starts-with builders work on both (Guid lower-cases first).
  // Everything else (Boolean, numeric, date, image, etc.) gets the
  // reduced operator set.
  return kuikaType === "String" || kuikaType === "Guid";
};

export const operatorsForColumn = (column: IDatabaseColumnModel): IOperatorOption[] => {
  const kuikaType: string | null | undefined = column.kuikaDataType;
  // Binary blob — only "is empty" / "not empty" because the value
  // input can't reasonably hold a base64 payload. The filter row
  // stays visible (presence/absence is the most-asked query) but
  // the picker hides every value-bound operator.
  if (isBinaryBlobKuikaType(kuikaType)) {
    return ALL_FILTER_OPERATORS.filter((o: IOperatorOption): boolean => BLOB_ALLOWED_OPERATORS.has(o.value));
  }
  // Boolean — two values, no ordering, no substring — only eq / neq /
  // is(Not)Null make sense.
  if (kuikaType === "Boolean") {
    return ALL_FILTER_OPERATORS.filter((o: IOperatorOption): boolean => BOOLEAN_ALLOWED_OPERATORS.has(o.value));
  }
  const textLike: boolean = isTextLikeKuikaType(kuikaType);
  const orderable: boolean = isOrderableKuikaType(kuikaType);
  return ALL_FILTER_OPERATORS.filter((o: IOperatorOption): boolean => {
    if (!textLike && TEXT_ONLY_OPERATORS.has(o.value)) {
      return false;
    }
    // gt / gte / lt / lte only show on orderable types. Strings are
    // technically orderable lexicographically, so we keep them on
    // text columns too (matches Linear "after / before" on text).
    if (!orderable && !textLike && (o.value === "gt" || o.value === "gte" || o.value === "lt" || o.value === "lte")) {
      return false;
    }
    return true;
  });
};

export const defaultOperatorForColumn = (column: IDatabaseColumnModel): ManagedDbFilterOperator => {
  const kuikaType: string | null | undefined = column.kuikaDataType;
  return isTextLikeKuikaType(kuikaType) ? "contains" : "eq";
};

// True when the column's filter picker should expose a "None" entry
// in addition to the operator options. Blob columns get this because
// every operator they expose is value-less (isNull / isNotNull), so
// without an explicit "no filter" choice the picker can't sit on a
// neutral state — picking either operator immediately filters the
// grid. Other column types have value-bound operators whose neutral
// state is naturally "operator picked, value empty", so they don't
// need the extra entry.
export const supportsNoneOperator = (column: IDatabaseColumnModel): boolean => {
  return isBinaryBlobKuikaType(column.kuikaDataType);
};

// Map the grid's per-column filter state into the wire-format the
// QueryRows / ExportRows endpoints accept. Drops entries that are
// undefined, and drops value-bound entries with an empty value (the
// filter row leaves the entry undefined when the input is empty, but
// this guards against a stray empty string from a future caller).
// Valueless operators (isNull / isNotNull) ride through with an empty
// value — the server doesn't bind a parameter for them.
export const buildColumnFilters = (filterMap: IColumnFilterMap): IManagedDbColumnFilter[] => {
  const result: IManagedDbColumnFilter[] = [];
  for (const column of Object.keys(filterMap)) {
    const entry: IColumnFilterEntry | undefined = filterMap[column];
    if (!entry) {
      continue;
    }
    if (isValuelessOperator(entry.operator)) {
      result.push({ column, operator: entry.operator, value: "" });
      continue;
    }
    const trimmed: string = entry.value.trim();
    if (trimmed.length === 0) {
      continue;
    }
    result.push({ column, operator: entry.operator, value: trimmed });
  }
  return result;
};
